import { onScopeDispose, toValue, type MaybeRefOrGetter } from 'vue';
import { useEventListener } from '@vueuse/core';
import { isOnControlInRow, targetElementOf } from '@/components/DataTable/rowClick';

/** How long a finger must rest on a row before it counts as a long press. */
export const LONG_PRESS_MS = 500;

/** How far a finger may drift during the press. Further than this is a scroll, not a press. */
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

export interface LongPressDelegateOptions {
    /** The element that holds the rows. One set of listeners on it covers every row inside. */
    container: MaybeRefOrGetter<HTMLElement | null | undefined>;
    /** False turns the delegate off, as on a wide screen. */
    enabled: MaybeRefOrGetter<boolean>;
    /** Whether the row with this `data-row-id` is still a loaded row. A row that is not is skipped. */
    isRowLoaded: (rowId: string) => boolean;
    /** Called once when a finger has rested on a row for `LONG_PRESS_MS`. */
    onLongPress: (rowId: string, event: PointerEvent) => void;
}

/**
 * Selects a row by a long press with a finger, on a phone.
 * It listens on the container rather than on each row, so it works on cards and on table rows
 * alike. Each row marks itself with `data-row-id`.
 *
 * A mouse press is ignored. A press on a link, a button or anything else under
 * `ROW_CLICK_IGNORE_SELECTOR` is left to that control. A control marked `data-row-click-opens`
 * is not left alone: a press on it selects the row, as `isOnControlInRow` says.
 * The browser sends a click when the finger lifts after a long press. That click is swallowed,
 * so the row is not toggled straight back off.
 */
export function useLongPressDelegate(options: LongPressDelegateOptions): void {
    let pressTimer: ReturnType<typeof setTimeout> | null = null;
    let pressStartX = 0;
    let pressStartY = 0;
    /** True from a touch pointerdown until the press is cancelled. It stays true after the press fires. */
    let touchPressHeld = false;
    let swallowNextClick = false;

    function cancelTimer() {
        if (pressTimer !== null) clearTimeout(pressTimer);
        pressTimer = null;
    }

    function cancelPress() {
        cancelTimer();
        touchPressHeld = false;
    }

    /** The row under the press, or null for a press outside a loaded row or on a control in the row. */
    function pressedRowId(target: EventTarget | null): string | null {
        const container = toValue(options.container);
        const targetElement = targetElementOf(target);
        if (!container || !targetElement) return null;
        const rowElement = targetElement.closest('[data-row-id]');
        if (!rowElement || !container.contains(rowElement)) return null;
        if (isOnControlInRow(targetElement, rowElement)) return null;
        const rowId = rowElement.getAttribute('data-row-id');
        if (!rowId || !options.isRowLoaded(rowId)) return null;
        return rowId;
    }

    useEventListener(options.container, 'pointerdown', (event: PointerEvent) => {
        swallowNextClick = false;
        cancelPress();
        if (event.pointerType !== 'touch' || !toValue(options.enabled)) return;
        // A press on a link or a button keeps the browser's own menu, such as "Open in new tab".
        if (pressedRowId(event.target) === null) return;
        touchPressHeld = true;
        pressStartX = event.clientX;
        pressStartY = event.clientY;
        pressTimer = setTimeout(() => {
            pressTimer = null;
            // The row is looked up when the press completes, so a row that went away meanwhile is skipped.
            if (!toValue(options.enabled)) return;
            const rowId = pressedRowId(event.target);
            if (rowId === null) return;
            swallowNextClick = true;
            options.onLongPress(rowId, event);
        }, LONG_PRESS_MS);
    });

    useEventListener(options.container, 'pointermove', (event: PointerEvent) => {
        if (pressTimer === null) return;
        const movedX = event.clientX - pressStartX;
        const movedY = event.clientY - pressStartY;
        if (Math.hypot(movedX, movedY) > LONG_PRESS_MOVE_TOLERANCE_PX) cancelPress();
    });

    // Lifting the finger ends a pending press. After a fired press it keeps the context menu
    // blocked, because some browsers send `contextmenu` only as the finger lifts.
    useEventListener(options.container, 'pointerup', cancelTimer);
    // A pointercancel after a fired press must not unblock the menu, so it only ends a pending press.
    useEventListener(options.container, 'pointercancel', () => {
        if (pressTimer !== null) cancelPress();
    });

    // The browser's own long-press menu would cover the row the press just selected.
    useEventListener(options.container, 'contextmenu', (event: MouseEvent) => {
        if (touchPressHeld) event.preventDefault();
    });

    // Capture phase, so the row's own click handler never sees the click.
    useEventListener(
        options.container,
        'click',
        (event: MouseEvent) => {
            if (!swallowNextClick) return;
            swallowNextClick = false;
            event.stopPropagation();
            event.preventDefault();
        },
        { capture: true },
    );

    onScopeDispose(cancelTimer);
}
